/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// Types
import { queryKeys } from '@config/constants/query-keys';
import { TApiError } from '@global-types/api/api';
import {
	TApiResponseCreateOrganization,
	TBodyCreateOrganization,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addOrganization = (
	body: TBodyCreateOrganization
): Promise<TApiResponseCreateOrganization> => {
	return apiClient.post('/organizations', body);
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseAddOrganizationOptions = {
	mutationConfig?: TMutationConfig<
		typeof addOrganization
	>;
	page: number;
	size: number;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAddOrganization = ({
	page,
	size,
	mutationConfig,
}: TUseAddOrganizationOptions) => {
	const { t } = useTranslation();
	const { onSuccess, onError, ...restConfig } =
		mutationConfig || {};
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addOrganization,
		onSuccess: async (...args) => {
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t(
					'organizations.notifications.organizationSuccessfullyAdded'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});

			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.organizations.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
